// Runs needing attention: waiting for you, running, failed, then the latest others.
import { ListChecks } from "@phosphor-icons/react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router";

import type { Environment, Run, Workflow } from "../api/client";
import { ago } from "../pages/format";
import { WidgetCard } from "../ui/Card";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { StatusPill } from "../ui/status";
import { runTone } from "../ui/tones";
import { usd } from "./format";
import { runsNeedingAttention } from "./runs";
import { useT } from "../i18n";

export function RunsWidget({
  runs,
  orgId,
  workflows,
  environments,
  className,
}: {
  runs: UseQueryResult<Run[], Error>;
  orgId: string | undefined;
  workflows: Workflow[];
  environments: Environment[];
  className?: string;
}) {
  const t = useT().dashboard.runs;
  const mine = (runs.data ?? []).filter((r) => orgId === undefined || r.org_id === orgId);
  const name = (r: Run) => workflows.find((w) => w.id === r.workflow_id);
  return (
    <WidgetCard
      className={className}
      title={t.title}
      action={
        <Link to="/runs" className="text-[13px] font-medium text-primary">
          {t.all}
        </Link>
      }
    >
      {runs.isPending ? <Loading /> : null}
      {runs.isError ? <ErrorState message={runs.error.message} /> : null}
      {runs.isSuccess && mine.length === 0 ? (
        <EmptyState compact icon={<ListChecks weight="duotone" />} title={t.none}>
          {t.noneBody}
        </EmptyState>
      ) : null}
      <ul className="-my-1 m-0 list-none divide-y divide-muted p-0">
        {runsNeedingAttention(mine).map((r) => {
          const workflow = name(r);
          const env = environments.find((e) => e.id === workflow?.environment_id);
          return (
            <li key={r.id}>
              <Link to={`/runs/${r.id}`} className="group flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-gray-900 group-hover:text-primary">{workflow?.name ?? t.run}</div>
                  <div className="truncate text-xs text-gray-500">
                    {[env?.name, ago(r.queued_at), r.cost_usd != null ? usd(r.cost_usd) : undefined].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <StatusPill tone={runTone(r.status)} status={r.status} />
              </Link>
            </li>
          );
        })}
      </ul>
    </WidgetCard>
  );
}
